/**
 * THROWAWAY DEMO SUPPORT CODE.
 *
 * Contract for the Pillar procurement demo's document Q&A panel
 * (frontend/src/demo/procurement/ask). The frontend sends the document text it
 * is already showing; the backend answers from that text only. Nothing is
 * persisted. Delete with frontend/src/demo/procurement (see the README there).
 */
import { oc } from "@orpc/contract";
import { z } from "zod";

/** Upper bound on the combined headings + text across all documents. */
export const ASK_DOCUMENT_MAX_TOTAL_CHARS = 150_000;
export const ASK_DOCUMENT_MAX_DOCUMENTS = 12;
export const ASK_DOCUMENT_MAX_HISTORY = 10;
export const ASK_DOCUMENT_MAX_QUESTION_CHARS = 1000;

export const AskDocumentSectionSchema = z.object({
  ref: z.string().min(1).max(64),
  heading: z.string().max(300),
  text: z.string().max(ASK_DOCUMENT_MAX_TOTAL_CHARS),
});

export const AskDocumentDocumentSchema = z.object({
  id: z.string().min(1).max(128),
  title: z.string().min(1).max(300),
  sections: z.array(AskDocumentSectionSchema).max(500),
});

export const AskDocumentHistoryMessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string().max(4000),
});

function countDocumentChars(
  documents: z.infer<typeof AskDocumentDocumentSchema>[],
): number {
  let total = 0;
  for (const document of documents) {
    for (const section of document.sections) {
      total += section.heading.length + section.text.length;
    }
  }
  return total;
}

export const AskDocumentInputSchema = z.object({
  question: z.string().trim().min(1).max(ASK_DOCUMENT_MAX_QUESTION_CHARS),
  documents: z
    .array(AskDocumentDocumentSchema)
    .min(1)
    .max(ASK_DOCUMENT_MAX_DOCUMENTS)
    .refine(
      (documents) =>
        countDocumentChars(documents) <= ASK_DOCUMENT_MAX_TOTAL_CHARS,
      {
        message: `Documents exceed ${ASK_DOCUMENT_MAX_TOTAL_CHARS} characters in total`,
      },
    ),
  history: z
    .array(AskDocumentHistoryMessageSchema)
    .max(ASK_DOCUMENT_MAX_HISTORY)
    .optional(),
});

export const AskDocumentCitationSchema = z.object({
  documentId: z.string(),
  ref: z.string(),
  /** Short verbatim excerpt from the cited section. */
  quote: z.string(),
});

// No length limits on the output on purpose: the frontend validates responses
// against this schema, and a too-long model answer must not look like a
// contract break. The service trims instead.
export const AskDocumentOutputSchema = z.object({
  /** Concise markdown: short paragraphs, "- " bullets, **bold**. */
  answer: z.string(),
  citations: z.array(AskDocumentCitationSchema),
  confidence: z.enum(["high", "medium", "low"]),
});

export type AskDocumentInput = z.infer<typeof AskDocumentInputSchema>;
export type AskDocumentOutput = z.infer<typeof AskDocumentOutputSchema>;
export type AskDocumentDocument = z.infer<typeof AskDocumentDocumentSchema>;
export type AskDocumentCitation = z.infer<typeof AskDocumentCitationSchema>;

export const procurementDemoContracts = {
  askDocument: oc
    .input(AskDocumentInputSchema)
    .output(AskDocumentOutputSchema)
    .route({
      method: "POST",
      summary:
        "Demo only: answer a question strictly from the provided procurement documents, with section citations.",
    }),
} as const;
